import {
  StoreSendRequestError,
} from '@services/errorHandling'

import {
  type ClubInfoExtended,
  type GetClubEnabledFeaturesRequest,
  type GetClubByHashRequest,
  type GetClubByShortNameRequest,
  type GetClubProfilesRequest,
  type GetClubsRequest,
  type GetClubPaymentsMethodsOnboardLinksRequest,
  type GetClubPaymentsOnboardRequest,
  type ClubEnabledFeatures,
  type ClubUserProfile,
  type ClubInfo,
  type ClubPaymentsMethodsOnboardLinks,
  type ClubPaymentsOnboard,
} from '@services/Club/declarations'

import {
  getClubEnabledFeaturesConfig,
  getClubByHashConfig,
  getClubByShortNameConfig,
  getClubProfilesConfig,
  getClubsConfig,
  getClubPaymentsMethodsOnboardLinksConfig,
  getClubPaymentsOnboardConfig,
} from '@services/Club/api'

import { useApiClubStore } from '@services/Club/store'

import { createRateLimitedRequest, type RequestOptions, watchRequest } from '@services/api/utils'

import { type WatchRequestConfig } from '@services/api/declarations'

// ***
// COMMON
// ***

const serviceName = 'Club'

const missingClubHashError = (caller: string) => new StoreSendRequestError({
  service: serviceName,
  message: 'clubHash param is required but not found',
  caller,
})

// ***
// REQUESTS
// ***

export function useApiClubStoreRequest() {
  const apiClubStore = useApiClubStore()
  const { sendRequest } = apiClubStore

  // FETCH GET CLUB ENABLED FEATURES
  function fetchGetClubEnabledFeatures(
    requestConfig: GetClubEnabledFeaturesRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetClubEnabledFeatures')

    createRateLimitedRequest<GetClubEnabledFeaturesRequest>({
      sendRequest,
      endpoint: getClubEnabledFeaturesConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubEnabledFeatures(
    { onSuccess, onError }: WatchRequestConfig<ClubEnabledFeatures>,
  ) {
    const { closeSubscription } = watchRequest<ClubEnabledFeatures>(getClubEnabledFeaturesConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET CLUB BY HASH
  function fetchGetClubByHash(
    requestConfig: GetClubByHashRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetClubByHash')

    createRateLimitedRequest<GetClubByHashRequest>({
      sendRequest,
      endpoint: getClubByHashConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubByHash(
    { onSuccess, onError }: WatchRequestConfig<ClubInfoExtended>,
  ) {
    const { closeSubscription } = watchRequest<ClubInfoExtended>(getClubByHashConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET CLUB BY SHORT NAME
  function fetchGetClubByShortName(
    requestConfig: GetClubByShortNameRequest,
    options?: RequestOptions,
  ) {
    const error = () => new StoreSendRequestError({
      service: serviceName,
      message: 'clubShortName param is required but not found',
      caller: 'fetchGetClubByShortName',
    })

    if (!requestConfig?.params?.clubShortName) throw error()

    createRateLimitedRequest<GetClubByShortNameRequest>({
      sendRequest,
      endpoint: getClubByShortNameConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubByShortName(
    { onSuccess, onError }: WatchRequestConfig<ClubInfo>,
  ) {
    const { closeSubscription } = watchRequest<ClubInfo>(getClubByShortNameConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET CLUB PROFILES
  function fetchGetClubProfiles(
    requestConfig: GetClubProfilesRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetClubProfiles')

    createRateLimitedRequest<GetClubProfilesRequest>({
      sendRequest,
      endpoint: getClubProfilesConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubProfiles(
    { onSuccess, onError }: WatchRequestConfig<ClubUserProfile[]>,
  ) {
    const { closeSubscription } = watchRequest<ClubUserProfile[]>(getClubProfilesConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET CLUBS
  function fetchGetClubs(requestConfig: GetClubsRequest, options?: RequestOptions) {
    createRateLimitedRequest<GetClubsRequest>({
      sendRequest,
      endpoint: getClubsConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubs(
    { onSuccess, onError }: WatchRequestConfig<ClubInfo[]>,
  ) {
    const { closeSubscription } = watchRequest<ClubInfo[]>(getClubsConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET CLUB PAYMENTS METHODS ONBOARD LINKS
  function fetchGetClubPaymentsMethodsOnboardLinks(
    requestConfig: GetClubPaymentsMethodsOnboardLinksRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetClubPaymentsMethodsOnboardLinks')

    createRateLimitedRequest<GetClubPaymentsMethodsOnboardLinksRequest>({
      sendRequest,
      endpoint: getClubPaymentsMethodsOnboardLinksConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubPaymentsMethodsOnboardLinks(
    { onSuccess, onError }: WatchRequestConfig<ClubPaymentsMethodsOnboardLinks>,
  ) {
    const { closeSubscription } = watchRequest<ClubPaymentsMethodsOnboardLinks>(
      getClubPaymentsMethodsOnboardLinksConfig,
      {
        onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
        onError: (error) => onError?.(error),
      },
    )

    return { closeSubscription }
  }

  // FETCH GET CLUB PAYMENTS ONBOARD
  function fetchGetClubPaymentsOnboard(
    requestConfig: GetClubPaymentsOnboardRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetClubPaymentsOnboard')

    createRateLimitedRequest<GetClubPaymentsOnboardRequest>({
      sendRequest,
      endpoint: getClubPaymentsOnboardConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetClubPaymentsOnboard(
    { onSuccess, onError }: WatchRequestConfig<ClubPaymentsOnboard>,
  ) {
    const { closeSubscription } = watchRequest<ClubPaymentsOnboard>(
      getClubPaymentsOnboardConfig,
      {
        onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
        onError: (error) => onError?.(error),
      },
    )

    return { closeSubscription }
  }

  return {
    fetchGetClubEnabledFeatures,
    fetchGetClubByHash,
    fetchGetClubByShortName,
    fetchGetClubProfiles,
    fetchGetClubs,
    fetchGetClubPaymentsMethodsOnboardLinks,
    fetchGetClubPaymentsOnboard,

    // watchers
    watchGetClubEnabledFeatures,
    watchGetClubByHash,
    watchGetClubByShortName,
    watchGetClubProfiles,
    watchGetClubs,
    watchGetClubPaymentsMethodsOnboardLinks,
    watchGetClubPaymentsOnboard,
  }
}
